import { FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Location, useLocation, useNavigate } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";

import { selectCurrentAccount, selectUserId } from "fond/api";
import ErrorPage from "fond/errorPage/ErrorPage";
import { useSwitchableAccounts } from "fond/hooks/useSwitchableAccounts";
import { NavigateError } from "fond/navigation/NavigateError";
import { selectIsSwitchingAccount, startAccountSwitch } from "fond/redux/accountContextSlice";
import { setAccountContext } from "fond/utils/accountContextStorage";
import { useAppSelector } from "fond/utils/hooks";
import { BlockSpinner } from "fond/widgets";
import { AccountSwitchDisplay } from "fond/widgets/AccountSwitch";

type LocationState = {
  targetAccountId?: string;
  targetAccountName?: string;
  returnPath?: string;
};

/**
 * Component shown when a user tries to access a resource that belongs to a different account
 */
const SwitchAccount: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation() as Location<LocationState>;
  const userId = useSelector(selectUserId);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const isSwitchingAccount = useAppSelector(selectIsSwitchingAccount);

  const { isLoading, accounts: switchAccounts } = useSwitchableAccounts();

  const { targetAccountId, targetAccountName, returnPath } = location.state || {};

  // Find the target account object from switchable accounts
  const targetAccount = targetAccountId ? switchAccounts.find((account) => account.ID === targetAccountId) : undefined;

  // Check if user has access to the target account
  const hasAccessToAccount = !!targetAccount;

  const handleSwitchAccount = () => {
    if (userId && targetAccountId) {
      dispatch(startAccountSwitch());
      setAccountContext(userId, targetAccountId);

      // Redirect to base URL with full origin to ensure clean navigation
      window.location.replace(returnPath || `${window.location.origin}/`);
    }
  };

  if (isLoading || isSwitchingAccount) return <BlockSpinner />;

  // If we don't have the information required to switch accounts, or the user doesn't have access to the target account, show an error page
  if (!isLoading && (!targetAccountId || !hasAccessToAccount || !currentAccount?.Name)) {
    return <NavigateError to="/404" />;
  }

  return (
    <ErrorPage
      title="Wrong Account"
      heading="Switch account"
      onGoBack={() => navigate("/")}
      message={
        <Box data-testid="switch-account-message">
          {hasAccessToAccount && currentAccount && targetAccount ? (
            <>
              <Typography mb={2}>The resource you're trying to access belongs to a different account.</Typography>
              <Box mb={3}>
                <AccountSwitchDisplay
                  currentAccount={currentAccount}
                  targetAccount={targetAccount}
                  warningMessage="Confirm if you want to switch accounts and load the resource?"
                />
              </Box>
              <Box mb={2}>
                <Button variant="contained" color="primary" onClick={handleSwitchAccount} data-testid="switch-account-button" fullWidth>
                  Switch to {targetAccount.Name}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography mb={1}>The resource you're trying to access belongs to a different account.</Typography>
              <Typography mb={2}>You don't have access to this account or it doesn't exist.</Typography>
            </>
          )}
          <Typography />
        </Box>
      }
    />
  );
};

export default SwitchAccount;
